import React, { useState, useEffect } from "react";
import { Save, Loader, CheckCircle, Globe, Image as ImageIcon } from "lucide-react";
import { cmsAPI, uploadAPI } from "../../services/api";
import { getImageUrl } from "../../utils/image";

const SEO_PAGES = [
  { id: "home", label: "Home Page" },
  { id: "about", label: "About Page" },
  { id: "services", label: "Our Services Page" },
  { id: "gallery", label: "Gallery Page" },
  { id: "products", label: "Products Listing Page" },
  { id: "product_detail", label: "Product Details (Default/Fallback)" },
  { id: "blog", label: "Blogs Listing Page" },
  { id: "blog_detail", label: "Blog Details (Default/Fallback)" },
  { id: "contact", label: "Contact Page" },
  { id: "privacy", label: "Privacy Policy Page" },
  { id: "terms", label: "Terms & Conditions Page" },
];

export default function AdminSEO() {
  const [activePage, setActivePage] = useState(SEO_PAGES[0]);
  const [seo, setSeo] = useState({ title: "", description: "", keywords: "", canonical: "", ogImage: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingOg, setUploadingOg] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSeo({ title: "", description: "", keywords: "", canonical: "", ogImage: "" });
    cmsAPI.getPage(activePage.id)
      .then(r => {
        const content = r.data.content || {};
        const seoData = content.seo || {};
        setSeo({
          title: seoData.title?.value || "",
          description: seoData.description?.value || "",
          keywords: seoData.keywords?.value || "",
          canonical: seoData.canonical?.value || "",
          ogImage: seoData.ogImage?.value || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activePage.id]);

  const handleOgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingOg(true);
    try {
      const { data } = await uploadAPI.single(file, "seo");
      setSeo(prev => ({ ...prev, ogImage: data.url }));
    } catch {
      alert("Image upload failed");
    } finally {
      setUploadingOg(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        "seo.title": { value: seo.title, type: "text" },
        "seo.description": { value: seo.description, type: "text" },
        "seo.keywords": { value: seo.keywords, type: "text" },
        "seo.canonical": { value: seo.canonical, type: "text" },
        "seo.ogImage": { value: seo.ogImage, type: "text" },
      };
      await cmsAPI.updatePage(activePage.id, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-gray-900 text-xl">SEO Management</h2>
          <p className="text-gray-400 text-sm">Optimize search engine visibility and meta tags per page</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="flex items-center gap-1.5 text-green-700 text-sm font-semibold bg-green-50 px-3 py-1.5 rounded-xl border border-green-200">
              <CheckCircle className="w-4 h-4" /> Saved!
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="btn-primary text-sm px-5 py-2.5"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" /> Save SEO Settings
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-card p-3 sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-3">Website Pages</p>
            {SEO_PAGES.map(page => (
              <button
                key={page.id}
                onClick={() => setActivePage(page)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left mb-1 ${
                  activePage.id === page.id
                    ? "bg-brand-pale text-brand-primary font-semibold"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                <Globe className="w-4 h-4 flex-shrink-0" />
                {page.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-2xl shadow-card">
              <Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" />
              <span className="text-gray-400 text-sm">Loading metadata...</span>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-card p-6 space-y-5">
              <h3 className="font-heading font-semibold text-gray-900 text-lg border-b border-gray-100 pb-3">
                SEO Settings for {activePage.label}
              </h3>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-600">Meta Title</label>
                    <span className={`text-xs ${seo.title.length > 60 ? "text-red-500" : "text-gray-400"}`}>
                      {seo.title.length}/60 characters (Recommended: 50-60)
                    </span>
                  </div>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. Sava Fresh - Premium Basmati Rice Sourcing"
                    value={seo.title}
                    onChange={e => setSeo(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-600">Meta Description</label>
                    <span className={`text-xs ${seo.description.length > 160 ? "text-red-500" : "text-gray-400"}`}>
                      {seo.description.length}/160 characters (Recommended: 120-160)
                    </span>
                  </div>
                  <textarea
                    className="input-field resize-none"
                    rows={4}
                    placeholder="Provide a detailed and compelling summary of the page for search engine results..."
                    value={seo.description}
                    onChange={e => setSeo(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Meta Keywords</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. basmati rice, food trading, saif zone uae, global sourcing"
                    value={seo.keywords}
                    onChange={e => setSeo(prev => ({ ...prev, keywords: e.target.value }))}
                  />
                  <p className="text-xs text-gray-400 mt-1">Separate keywords or phrases with commas.</p>
                </div>

                <div className="pt-2 border-t border-gray-100">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Canonical URL (Optional)</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. https://savafresh.com/products"
                    value={seo.canonical}
                    onChange={e => setSeo(prev => ({ ...prev, canonical: e.target.value }))}
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave empty to use the default URL for this page.</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Social Share / OpenGraph Image</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="Image URL for Facebook, LinkedIn, Twitter preview..."
                      value={seo.ogImage}
                      onChange={e => setSeo(prev => ({ ...prev, ogImage: e.target.value }))}
                    />
                    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer transition-colors ${uploadingOg ? "opacity-50 pointer-events-none" : "hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
                      {uploadingOg ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                      {uploadingOg ? "Uploading..." : "Upload Image"}
                      <input type="file" accept="image/*" className="hidden" onChange={handleOgUpload} disabled={uploadingOg} />
                    </label>
                  </div>
                  {seo.ogImage && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-xl inline-block border border-gray-100">
                      <img src={getImageUrl(seo.ogImage)} alt="OG Preview" className="h-20 w-auto rounded-lg object-cover" onError={e => { e.target.style.display = "none"; }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Box */}
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 mt-6">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Google Search Preview</h4>
                <div className="space-y-1 font-sans">
                  <div className="text-blue-800 hover:underline text-lg font-medium cursor-pointer line-clamp-1">
                    {seo.title || "Please enter a Meta Title"}
                  </div>
                  <div className="text-green-700 text-xs truncate">
                    https://savafresh.com/{activePage.id === "home" ? "" : activePage.id}
                  </div>
                  <div className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                    {seo.description || "Please enter a Meta Description to preview how your page will display in search results."}
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
